import { useState, useEffect } from 'react';
import { FiUpload, FiImage, FiEdit2, FiCheck, FiX } from 'react-icons/fi';
import { toast } from 'react-hot-toast';
import api from '../../../../api/api';

const IndexBannerTab = () => {
  const [isEditingBanner, setIsEditingBanner] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [banner, setBanner] = useState({
    url: '',
    file: null
  });

  useEffect(() => {
    fetchBanner();
  }, []);

  const fetchBanner = async () => {
    try {
      setIsLoading(true);
      const response = await api.getIndexBanner();
      if (response?.banner_image) {  // Updated to match backend response
        setBanner({
          url: response.banner_image,
          file: null
        });
      }
    } catch (error) {
      toast.error('Failed to fetch index banner');
      console.error('Error fetching index banner:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBannerChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size should be less than 5MB');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setBanner({
          url: reader.result,
          file: file
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveBanner = async () => {
    try {
      if (!banner.file) {
        toast.error('Please select a new banner to upload');
        return;
      }

      const formData = new FormData();
      formData.append('banner_image', banner.file);  // Updated to match backend expected field

      await api.updateIndexBanner(formData);
      setIsEditingBanner(false);
      toast.success('Index banner updated successfully');
      await fetchBanner();
    } catch (error) {
      toast.error('Failed to update index banner');
      console.error('Error updating index banner:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="bg-white rounded-xl shadow-sm p-3 sm:p-4 md:p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Index Banner</h3>
          <button
            onClick={() => setIsEditingBanner(!isEditingBanner)}
            className="text-indigo-600 hover:text-indigo-700"
          >
            {isEditingBanner ? <FiX size={24} /> : <FiEdit2 size={20} />}
          </button>
        </div>

        <div className="space-y-4">
          <div className="border rounded-lg p-4 bg-gray-50">
            {banner.url ? (
              <img
                src={banner.url}
                alt="Index Banner"
                className="w-full h-48 object-cover rounded-lg"
              />
            ) : (
              <div className="flex items-center justify-center h-48 bg-gray-100 rounded-lg">
                <FiImage className="w-8 h-8 text-gray-400" />
              </div>
            )}
          </div>

          {isEditingBanner && (
            <div className="space-y-4">
              <div className="relative">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleBannerChange}
                  className="w-full p-2 border rounded-lg"
                />
                <FiUpload className="absolute right-3 top-3 text-gray-400" />
              </div>

              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => {
                    setIsEditingBanner(false);
                    setBanner(prev => ({ ...prev, file: null }));
                  }}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveBanner}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2"
                >
                  <FiCheck className="w-4 h-4" />
                  Save Changes
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default IndexBannerTab;

